/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect, useState, useRef } from 'react';
import get from 'lodash/get';
import { AccessType } from '@quintype/components';
import { useSelector } from 'react-redux';
import { SvgHandler } from '../../../svg-handler/svg-handler';
import { getPath, getTextContrast } from '../../../../utils';
import downChevron from '../../../../../assets/icons/down-arrow.svg';
import LoaderOverlay from '../../../molecules/loader-overlay';
import { Attribute } from '../attribute';
import defaultImage from '../../../../../assets/icons/default-image.svg';
import { useShowLoaderAndSignInMsg } from '../custom-hooks';
import { SignInMessage } from '../../../atoms/sign-in-message';
import { InvalidLink } from '../molecules/invalid-link';
import { setMaxHeightForRefs } from '../layout-utils';
import { textWithFallback } from '../helpers';
import { NotFoundPage } from '../../not-found';
import './styles.m.css';

const FALLBACK_PAGE_TITLE = '<p>Available Plans</p>';
const FALLBACK_PAGE_DESCRIPTION =
  '<p>Explore the available plans you can switch to. Find new options that perfectly match your needs and preferences.</p>';
const FALLBACK_CHOOSE_PLAN_BTN = '<p>Choose Plan</p>';
const FALLBACK_NOTE =
  '<p>^ Based on your current subscription, we have applied a prorated discount. The discount is applicable only for the first billing. From the next billing you’ll be charged the base price of the plan.</p>';
const FALLBACK_WHATS_INCLUDED = "<p>What's Included</p>";

const getTagData = planData => {
  const planCustomAttributes = get(planData, ['custom_attributes']);
  let tagData = planCustomAttributes.find(attr => attr.name === 'Tag');
  return tagData ? [tagData] : [];
};
const PlanImage = ({ plan, showImage }) => {
  if (!plan || !showImage) return null;
  const planImage = get(plan, ['image_url']);
  return (
    <div styleName='plan-image-wrapper'>
      {planImage ? (
        <img src={planImage} alt={plan.title} />
      ) : (
        <SvgHandler xlinkHref={defaultImage} width='100%' height='100%' />
      )}
    </div>
  );
};
const getPlanChangeOverviewLink = ({
  fromPlanId,
  toPlanId,
  subscriptionId,
  type,
  redirectUrl,
  target,
  sketchesHost,
  whiteListedUrlParams
}) => {
  if (target !== 'web') return false;
    return getPath({
      sketchesHost,
      relativePath: '/plan-change-overview',
      defaultParams: redirectUrl ? `from=${fromPlanId}&to=${toPlanId}&subscriptionId=${subscriptionId}&type=${type}&redirectUrl=${redirectUrl}` : `from=${fromPlanId}&to=${toPlanId}&subscriptionId=${subscriptionId}&type=${type}`,
      injectCustomParams: true,
      whiteListedUrlParams,
      swapDefaultCustomParams : true
    });

};

const PlanDescriptionAccordion = ({ whatsIncludedTxt, planDescription, index }) => {
  const [windowWidth, setWindowWidth] = useState(0);
  const [showWhatsIncluded, setShowWhatsIncluded] = useState(false);

  useEffect(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  const isMobile = windowWidth < 767;
  const whatsIncludedDropdownHandler = () => {
    if (!isMobile) return;
    setShowWhatsIncluded(!showWhatsIncluded);
  };

  return (
    <div styleName='whats-included-wrapper'>
      <div styleName='whats-included-accordion' onClick={whatsIncludedDropdownHandler}>
        <div
          data-test-id={`avl-plans-dispplan-whats-included-${index}`}
          dangerouslySetInnerHTML={{ __html: textWithFallback(whatsIncludedTxt, FALLBACK_WHATS_INCLUDED) }}
        ></div>
        {isMobile ? (
          <div
            style={{ ...(showWhatsIncluded ? { transform: 'rotate(180deg)' } : {}) }}
            styleName='whats-included-icon-wrapper'
          >
            <SvgHandler xlinkHref={downChevron} width='24' height='24' />
          </div>
        ) : null}
      </div>
      {!isMobile || (isMobile && showWhatsIncluded) ? (
        <div
          data-test-id={`avl-plans-dispplan-description-${index}`}
          styleName='plan-description'
          dangerouslySetInnerHTML={{ __html: textWithFallback(planDescription, '') }}
        />
      ) : null}
    </div>
  );
};

const DisplayPlans = ({
  plansToDisplay,
  planId,
  subscriptionId,
  allPlans,
  pageSettings,
  mode,
  subscriptionConfig = {},
  redirectUrl,
  target,
  sketchesHost,
  whiteListedUrlParams
}) => {
  if (!plansToDisplay) return null;
  const { choosePlanBtn, showPlanImages } = pageSettings;
  const onePlanPresent = plansToDisplay.length === 1;
  const twoPlansPresent = plansToDisplay.length === 2;
  const {
    buttonColor = '#4860BC',
    darkButtonColor = '#4860BC',
    descriptionTitle: whatsIncluded,
    showOfferPrice = true
  } = subscriptionConfig;
  const choosePlanBtnInlineStyles = {
    background: buttonColor,
    color: getTextContrast(buttonColor) === 'dark' ? '#000' : '#fff'
  };
  if (mode === 'dark') {
    choosePlanBtnInlineStyles.background = darkButtonColor;
    choosePlanBtnInlineStyles.color = getTextContrast(darkButtonColor) === 'dark' ? '#000' : '#fff';
  }

  const planRefs = plansToDisplay.map(() => useRef(null));
  useEffect(() => {
    setMaxHeightForRefs(planRefs);
  }, [plansToDisplay, planRefs]);

  return (
    <div
      data-test-id='avl-plans-wrapper'
      styleName={`display-plans-wrapper ${onePlanPresent ? 'one-plan-to-show' : ''} ${
        twoPlansPresent ? 'two-plans-to-show' : ''
      }`}
    >
      {plansToDisplay.map((plan, index) => {
        const planData = allPlans.find(p => p.id === plan.id);
        if (!planData) return null;
        const tagData = getTagData(planData);
        const {
          id,
          title,
          duration_length: durationLength,
          duration_unit: durationUnit,
          price_cents: priceCents,
          price_currency: priceCurrency,
          prorated_amount_cents: proratedAmountCents
        } = plan;
        const planDescription = get(planData, ['description']);
        return (
          <div key={id} styleName={`display-plan ${mode}`} data-test-id={`avl-plans-dispplan`}>
            <Attribute data={{ custom_attributes: tagData }} attributeName='Tag' />
            <div data-test-id={`avl-plans-dispplan-title-${index}`} styleName='plan-title' ref={planRefs[index]}>
              {title}
            </div>
            <PlanImage data-test-id={`avl-plans-dispplan-image-${index}`} plan={planData} showImage={showPlanImages} />
            <div styleName='plan-price-line'>
              {proratedAmountCents === priceCents ? (
                <></>
              ) : (
                <span styleName='plan-price'>{`${priceCurrency} ${priceCents / 100}`}</span>
              )}
              <span
                styleName='plan-price-to-pay'
                data-test-id={`avl-plans-dispplan-price-${index}`}
              >{`${priceCurrency} ${proratedAmountCents / 100}`}</span>
              {proratedAmountCents === priceCents ? <></> : <sup>{`^`}</sup>}
            </div>
            <div
              data-test-id={`avl-plans-dispplan-duration-${index}`}
              styleName='plan-duration'
            >{`${durationLength} ${durationUnit}`}</div>
            {showOfferPrice && (
              <div styleName={`offer-price-wrapper ${mode}`}>
                <Attribute data={planData} attributeName='Actual Price' />
                <Attribute data={planData} attributeName='Discount' />
              </div>
            )}
            <a
              style={choosePlanBtnInlineStyles}
              data-test-id={`avl-plans-dispplan-choose-btn-${index}`}
              styleName='choose-plan-btn'
              href={getPlanChangeOverviewLink({
                fromPlanId: planId,
                toPlanId: planData.id,
                subscriptionId,
                type: plan['plan-change-type'],
                redirectUrl,
                target,
                sketchesHost,
                whiteListedUrlParams
              })}
              dangerouslySetInnerHTML={{ __html: textWithFallback(choosePlanBtn, FALLBACK_CHOOSE_PLAN_BTN) }}
            ></a>
            <PlanDescriptionAccordion
              index={index}
              whatsIncludedTxt={whatsIncluded}
              planDescription={planDescription}
            />
          </div>
        );
      })}
    </div>
  );
};

export const ShowAvailablePlans = ({
  getSubscriptionsWithSwitchablePlans,
  subscriptionId,
  planId,
  enableAccesstype,
  initAccessType,
  isAtGlobalSet,
  mode = 'light',
  getSubscription,
  redirectUrl,
  target = 'web'
}) => {
  const { showLoader, showSignInMessage } = useShowLoaderAndSignInMsg();
  const [availablePlans, setAvailablePlans] = useState();
  const [allPlans, setAllPlans] = useState([]);
  const [subscriptionsFetched, setSubscriptionsFetched] = useState(false);
  const availablePlansConfig = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'available-plans', 'rows', '0', 'config'], {})
  );
  const subscriptionConfig = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'subscription', 'pageSetting'], {})
  );

  const sketchesHost = useSelector(state => get(state, ['qt', 'config', 'sketches-host'], ''));
  const whiteListedUrlParams = useSelector((state) => get(state, ["qt", "config", "pagebuilder-config", "general", "others", "whiteListedUrlParams"], []));

  useEffect(() => {
    initAccessType(async () => {
      if (enableAccesstype && isAtGlobalSet && !availablePlans) {
        const data = await getSubscriptionsWithSwitchablePlans(subscriptionId);
        setSubscriptionsFetched(true);
        const upgradePlanDetails = (get(data, ['subscription', 'upgrade_plan_details'], []) || []).map(planData => {
          return { ...planData, 'plan-change-type': 'upgrade' };
        });
        const downgradePlanDetails = (get(data, ['subscription', 'downgrade_plan_details'], []) || []).map(planData => {
          return { ...planData, 'plan-change-type': 'downgrade' };
        });
        const crossgradePlanDetails = (get(data, ['subscription', 'crossgrade_plan_details'], []) || []).map(
          planData => {
            return { ...planData, 'plan-change-type': 'crossgrade' };
          }
        );
        setAvailablePlans([...upgradePlanDetails, ...crossgradePlanDetails, ...downgradePlanDetails]);

        const subscription = (await getSubscription()) || [];
        const plans = subscription.map(sub => sub['subscription_plans']).flat();
        setAllPlans(plans);
      }
    });
  }, [enableAccesstype, isAtGlobalSet, availablePlans, allPlans]);
  const { pageTitle, pageDescription, note } = availablePlansConfig;

  if (showSignInMessage) return <SignInMessage />;
  if (showLoader || !availablePlans) return <LoaderOverlay />;
  if (subscriptionsFetched && (!availablePlans || !availablePlans.length)) return <InvalidLink />;

  return (
    <div styleName={`top-lvl-wrapper ${mode}`}>
      <div styleName='page-content'>
        <h1
          data-test-id='avl-plans-h1'
          styleName='hidden'
          dangerouslySetInnerHTML={{ __html: textWithFallback(pageTitle, FALLBACK_PAGE_TITLE) }}
        ></h1>
        <div
          data-test-id='avl-plans-page-title'
          styleName='page-title'
          dangerouslySetInnerHTML={{ __html: textWithFallback(pageTitle, FALLBACK_PAGE_TITLE) }}
        ></div>
        <div
          data-test-id='avl-plans-page-description'
          styleName='page-description'
          dangerouslySetInnerHTML={{ __html: textWithFallback(pageDescription, FALLBACK_PAGE_DESCRIPTION) }}
        ></div>
        <div>
          <DisplayPlans
            plansToDisplay={availablePlans}
            allPlans={allPlans}
            pageSettings={availablePlansConfig}
            mode={mode}
            subscriptionConfig={subscriptionConfig}
            planId={planId}
            subscriptionId={subscriptionId}
            redirectUrl={redirectUrl}
            target={target}
            sketchesHost={sketchesHost}
            whiteListedUrlParams={whiteListedUrlParams}
          />
        </div>
        <div
          data-test-id='avl-plans-note'
          styleName='note'
          dangerouslySetInnerHTML={{ __html: textWithFallback(note, FALLBACK_NOTE) }}
        ></div>
      </div>
    </div>
  );
};

export const AvailablePlans = props => {
  const [isAtGlobalSet, setIsAtGlobalSet] = useState(false);
  const member = useSelector(state => get(state, ['member']));
  const enableDarkMode = useSelector(state => get(state, ['header', 'isDarkModeEnabled'], false));
  const accessTypePbConfig = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'general', 'accesstypeIntegration'], {})
  );

  const redirectUrl = get(props, ['data', 'redirectUrl']);
  const subscriptionId = get(props, ['data', 'subscriptionId']);
  const planId = get(props, ['data', 'planId']);
  if (!subscriptionId || !planId) return <InvalidLink />;

  const mode = enableDarkMode ? 'dark' : 'light';
  const { email = '', phone = '' } = member || {};
  const isStaging = get(accessTypePbConfig, ['accesstypeMode']) === 'staging';
  const {
    accesstypeKey = '',
    bridgeKeeperIntegrationId = '',
    enableAccesstype = false,
    accessTypeProdHost = '',
    accessTypeStgHost = ''
  } = accessTypePbConfig;
  if (!enableAccesstype) return <NotFoundPage />;

  const sketchesHost = useSelector(state => get(state, ['qt', 'config', 'sketches-host'], {}));
  return (
    <AccessType
      enableAccesstype={enableAccesstype}
      isStaging={isStaging}
      accessTypeKey={accesstypeKey}
      accessTypeBkIntegrationId={bridgeKeeperIntegrationId}
      email={email}
      phone={phone}
      storyId={''}
      sketchesHost={sketchesHost}
      prodHost={accessTypeProdHost}
      stagingHost={accessTypeStgHost}
      onATGlobalSet={() => {
        setIsAtGlobalSet(true);
      }}
    >
      {({ initAccessType, getSubscriptionsWithSwitchablePlans, getSubscription }) => (
        <ShowAvailablePlans
          enableAccesstype={enableAccesstype}
          getSubscriptionsWithSwitchablePlans={getSubscriptionsWithSwitchablePlans}
          subscriptionId={subscriptionId}
          planId={planId}
          isAtGlobalSet={isAtGlobalSet}
          initAccessType={initAccessType}
          mode={mode}
          getSubscription={getSubscription}
          redirectUrl={redirectUrl}
        />
      )}
    </AccessType>
  );
};
